import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from '@angular/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';
import { FuseProgressBarModule, FuseSidebarModule, FuseThemeOptionsModule } from '@fuse/components';
import { FuseModule } from '@fuse/fuse.module';
import { FuseSharedModule } from '@fuse/shared.module';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { InMemoryWebApiModule } from 'angular-in-memory-web-api';
import { AppInitializationService } from 'app/app-initialization.service';
import { AppComponent } from 'app/app.component';
import { fuseConfig } from 'app/fuse-config';
import { LayoutModule } from 'app/layout/layout.module';
import 'hammerjs';
import { ChartsModule } from 'ng2-charts';
import { AuthGuard } from './guards/auth-guard';
import { VerticalLayout1Component } from './layout/vertical/layout-1/layout-1.component';
import { CarListComponent } from './main/apps/dashboards/car/car-list/car-list.component';
import { CarModule } from './main/apps/dashboards/car/car.module';
import { CompanyListComponent } from './main/apps/dashboards/company/company-list/company-list.component';
import { CompanyModule } from './main/apps/dashboards/company/company.module';
import { ContainerListComponent } from './main/apps/dashboards/container/container-list/container-list.component';
import { ContainerModule } from './main/apps/dashboards/container/container.module';
import { DashboardComponent } from './main/apps/dashboards/dashboard/dashboard.component';
import { DashboardModule } from './main/apps/dashboards/dashboard/dashboard.module';
import { DeviceListComponent } from './main/apps/dashboards/devices/device-list/device-list.component';
import { DeviceViewComponent } from './main/apps/dashboards/devices/device-view/device-view.component';
import { DevicesModule } from './main/apps/dashboards/devices/devices.module';
import { FirmwareDetailComponent } from './main/apps/dashboards/firmware/firmware-detail/firmware-detail.component';
import { FirmwareListComponent } from './main/apps/dashboards/firmware/firmware-list/firmware-list.component';
import { FirmwareModule } from './main/apps/dashboards/firmware/firmware.module';
import { LoginComponent } from './main/apps/dashboards/login/login.component';
import { LoginModule } from './main/apps/dashboards/login/login.module';
import { DeviceLogComponent } from './main/apps/dashboards/logs/device-log/device-log.component';
import { LogsModule } from './main/apps/dashboards/logs/logs.module';
import { QueryLogComponent } from './main/apps/dashboards/logs/query-log/query-log.component';
import { SessionLogComponent } from './main/apps/dashboards/logs/session-log/session-log.component';
import { DriverProcessComponent} from './main/apps/dashboards/logs/driver-process/driver-process.component';
import { DriverMapComponent } from './main/apps/dashboards/map/driver-map/driver-map.component';
import { MapModule } from './main/apps/dashboards/map/map.module';
import { MapComponent } from './main/apps/dashboards/map/maps/map.component';
import { SimulationComponent } from './main/apps/dashboards/simulation-map/simulation/simulation.component';
import { RoleListComponent } from './main/apps/dashboards/role/role-list/role-list.component';
import { RoleModule } from './main/apps/dashboards/role/role.module';
import { ShiftListComponent } from './main/apps/dashboards/shift/shift-list/shift-list.component';
import { ShiftModule } from './main/apps/dashboards/shift/shift.module';
import { UserListComponent } from './main/apps/dashboards/user/user-list/user-list.component';
import { UserModule } from './main/apps/dashboards/user/user.module';
import { ZoneListComponent } from './main/apps/dashboards/zone/zone-list/zone-list.component';
import { ZoneModule } from './main/apps/dashboards/zone/zone.module';
import { Error404Component } from './main/pages/errors/404/error-404.component';
import { Error404Module } from './main/pages/errors/404/error-404.module';
import { CarService } from './services/car.service';
import { CompanyService } from './services/company.service';
import { ContainerService } from './services/container.service';
import { DeviceService } from './services/device.service';
import { FirmwareService } from './services/firmware.service';
import { LogsService } from './services/logs.service';
import { UserService } from './services/user.service';
import { EnvanterListComponent } from './main/apps/dashboards/envanter/envanter-list/envanter-list.component';
import { EnvanterModule } from './main/apps/dashboards/envanter/envanter.module';
import { ReportingService } from './services/reporting.service';
import { ContainerViewComponent } from './main/apps/dashboards/container/container-view/container-view.component';
import { SimulationMapComponent } from './main/apps/dashboards/simulation-map/maps/map.component';
import { SimulationMapModule } from './main/apps/dashboards/simulation-map/simulation-map.module';
import { SimulationCarListComponent } from './main/apps/dashboards/simulation-map/car/car-list/car-list.component';
import { SimulationContainerListComponent } from './main/apps/dashboards/simulation-map/container/container-list/container-list.component';
import { SimulationZoneListComponent } from './main/apps/dashboards/simulation-map/zone/zone-list/zone-list.component';
import { SimulationReportingComponent } from './main/apps/dashboards/simulation-map/simulation-reporting/simulation-reporting.component';
import { WarnNotificationsModule } from './main/apps/dashboards/warn-notifications/warn-notifications.module';
import { WarnNotificationsComponent } from './main/apps/dashboards/warn-notifications/warn-notifications/warn-notifications.component';
import { SimulationService } from './services/simulation.service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ReportModule } from './main/apps/dashboards/report/report.module';
import { ReportListComponent } from './main/apps/dashboards/report/report-list.component';
import { CoordinatesFromAddressService } from './services/coordinates-from-address.service';
import { FastTaskProcessingModule } from './main/apps/dashboards/fast-task-processing/fast-task-processing.module';
import { FastTaskProcessingComponent } from './main/apps/dashboards/fast-task-processing/fast-task-processing/fast-task-processing.component';
import { FastTaskProcessingService } from './services/fast-task-processing.service';
import { GeneralReportModule } from './main/apps/dashboards/general-report/general-report.module';
import { GeneralReportService } from './services/general-report.service';
import { GeneralContainerReportListComponent } from './main/apps/dashboards/general-report/general-container-report-list/general-container-report-list.component';
import { GeneralZoneReportListComponent } from './main/apps/dashboards/general-report/general-zone-report-list/general-zone-report-list.component';
import { GeneralReportListComponent } from './main/apps/dashboards/general-report/general-report-list/general-report-list.component';
import { SimulationTestComponent } from './main/apps/dashboards/simulation-map/simulation-test/simulation-test.component';
import { SimulationGeneralReportListComponent } from './main/apps/dashboards/simulation-map/simulation-general-report/simulation-general-report-list/simulation-general-report-list.component';
import { SimulationGeneralContainerReportListComponent } from './main/apps/dashboards/simulation-map/simulation-general-report/simulation-general-container-report-list/simulation-general-container-report-list.component';
import { SimulationGeneralZoneReportListComponent } from './main/apps/dashboards/simulation-map/simulation-general-report/simulation-general-zone-report-list/simulation-general-zone-report-list.component';
import { SimulationGeneralReportModule } from './main/apps/dashboards/simulation-map/simulation-general-report/simulation-general-report.module';
import { RouteHistory } from './models/route-history.model';
import { RouteHistoryService } from './services/route-history.service';



const appRoutes: Routes = [
    {
        path: 'auth/login',
        component: LoginComponent
    },
    {
        path: '',
        component: VerticalLayout1Component,
        canActivateChild: [AuthGuard],
        children: [
            {
                path: '',
                component: DashboardComponent
            },
            {
                path: 'apps/dashboard',
                component: DashboardComponent,
            },
            {
                path: 'apps/devices',
                component: DeviceListComponent,
            },
            {
                path: 'apps/devices/view',
                component: DeviceViewComponent,
            },
            {
                path: 'apps/firmware',
                component: FirmwareListComponent,
            },
            {
                path: 'apps/firmware/packet',
                component: FirmwareDetailComponent,
            },
            {
                path: 'apps/car',
                component: CarListComponent,
            },
            {
                path: 'apps/container',
                component: ContainerListComponent,
            },
            {
                path: 'apps/container/view',
                component: ContainerViewComponent,
            },
            {
                path: 'apps/user',
                component: UserListComponent,
            },
            {
                path: 'apps/zone',
                component: ZoneListComponent,
            },
            {
                path: 'apps/device-log',
                component: DeviceLogComponent,
            },
            {
                path: 'apps/session-log',
                component: SessionLogComponent,
            },
            {
                path: 'apps/query-log',
                component: QueryLogComponent,
            },
            {
                path: 'apps/driver-process',
                component: DriverProcessComponent,
            },
            {
                path: 'apps/map',
                component: MapComponent,
            },
            {
                path: 'apps/general-container-report-list',
                component: GeneralContainerReportListComponent,
            },
            {
                path: 'apps/general-zone-report-list',
                component: GeneralZoneReportListComponent,
            },
            {
                path: 'apps/general-report-list',
                component: GeneralReportListComponent,
            },
            {
                path: 'apps/company',

                component: CompanyListComponent,
            },
            {
                path: 'apps/fast-task-processing',

                component: FastTaskProcessingComponent,
            },
            {
                path: 'apps/role',
                component: RoleListComponent,
            },
            {
                path: 'apps/simulation',
                component: SimulationTestComponent  ,
            },
            {
                path: 'apps/simulation-general-report',
                component: SimulationGeneralReportListComponent,
            },
            {
                path: 'apps/simulation-general-container-report',
                component: SimulationGeneralContainerReportListComponent,
            },
            {
                path: 'apps/simulation-general-zone-report',
                component: SimulationGeneralZoneReportListComponent,
            },
            {
                path: 'apps/simulation-map',
                component: SimulationMapComponent,
            },
            {
                path: 'apps/simulation-car',
                component: SimulationCarListComponent,
            },
            {
                path: 'apps/simulation-container',
                component: SimulationContainerListComponent,
            },
            {
                path: 'apps/simulation-zone',
                component: SimulationZoneListComponent,
            },
            {
                path: 'apps/simulation-reporting',
                component: SimulationReportingComponent,
            },
            {
                path: 'apps/shift',
                component: ShiftListComponent,
            },
            {
                path: 'apps/driver-map',
                component: DriverMapComponent,
            },
            {
                path: 'apps/envanter-list',
                component: EnvanterListComponent,
            },
            {
                path: 'apps/report-list',
                component: ReportListComponent,
            },
            {
                path: 'apps/warn-notifications',
                component: WarnNotificationsComponent,
            },
        ]
    },
    {
        path: '**', pathMatch: 'full',
        component: Error404Component
    },
]

@NgModule({
    declarations: [
        AppComponent
    ],
    imports: [
        FastTaskProcessingModule,
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        LoginModule,
        DevicesModule,
        DashboardModule,
        ShiftModule,
        CarModule,
        GeneralReportModule,
        UserModule,
        SimulationMapModule,
        SimulationGeneralReportModule,
        EnvanterModule,
        CompanyModule,
        ZoneModule,
        RoleModule,
        ReportModule,
        NgxSpinnerModule,
        WarnNotificationsModule,
        ChartsModule,
        LogsModule,
        MapModule,
        ContainerModule,
        FirmwareModule,
        Error404Module,
        MatDialogModule,
        RouterModule.forRoot(appRoutes),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        // Material moment date module
        MatMomentDateModule,

        // Material
        MatButtonModule,
        MatIconModule,

        // Fuse modules
        FuseModule.forRoot(fuseConfig),
        FuseProgressBarModule,
        FuseSharedModule,
        FuseSidebarModule,
        FuseThemeOptionsModule,

        // App modules
        LayoutModule
    ],
    providers: [
        AppInitializationService,
        {
            provide: APP_INITIALIZER,
            multi: true,
            deps: [AppInitializationService],
            useFactory: (appConfigService: AppInitializationService) => {
                return () => {                    
                    return appConfigService.loadAppConfig();
                };
            },
        },
        {
            
            provide: MAT_DATE_LOCALE, useValue: "tr"
        },
        CarService,
        ContainerService,
        SimulationService,
        CoordinatesFromAddressService,
        UserService,
        GeneralReportService,
        FirmwareService,
        RouteHistoryService,
        ReportingService,
        DeviceService,
        FastTaskProcessingService,
        LogsService,
        CompanyService,
        AuthGuard
    ],
    bootstrap: [
        AppComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
